// ============================================================
// discoveryData — live data layer for the V2 Customer Discovery workroom.
//
// PHASE_2M P9: invites come ONLY from sq_intake_invites via
// GET /api/square/command-center/intake-invites; submissions come ONLY
// from sq_support_requests (intake_invite_id set) via
// GET /api/square/command-center/intake-invites/submissions.
// NO seeded rows, ever. No bearer token → authRequired; a backend
// failure surfaces as an honest error; an empty result is an honest
// empty state. Mirrors commsData.useApiList / useDailyMeetings.
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, isAuthError } from '../../../api/client'

// Row shape returned by GET /api/square/command-center/intake-invites —
// a direct sq_intake_invites selection (see
// api/square/command-center/intake-invites.ts).
export interface DiscoveryInvite {
  id: string
  invite_code: string
  invite_type: 'website' | 'software' | 'presentation' | 'other' | 'custom' | string
  title: string
  description: string | null
  questions_json: Array<Record<string, unknown>>
  audience: string | null
  target_project_id: string | null
  status: 'active' | 'closed' | string
  allow_attachments: boolean
  expires_at: string | null
  created_by: string
  submission_count: number | null
  created_at: string
  updated_at: string
}

// submitter_json shape written by the public receiver
// (api/square/intake/submit.ts): { name, email, answers, attachments }.
export interface DiscoverySubmitter {
  name?: string
  email?: string
  answers?: Record<string, unknown>
  attachments?: Array<{ name: string; size?: string | null; type?: string | null }>
}

// Row shape returned by GET .../intake-invites/submissions — an
// sq_support_requests row joined with its invite + submitter_json.
// Status vocabulary: 'open' on arrival, then 'pending' | 'resolved' |
// 'closed' via the decide action.
export interface DiscoverySubmission {
  id: string
  subject: string
  body: string
  status: 'open' | 'pending' | 'resolved' | 'closed' | string
  created_at: string
  invite: { title: string; invite_code: string } | null
  submitter: DiscoverySubmitter | null
}

export interface DiscoveryState {
  invites: DiscoveryInvite[]
  submissions: DiscoverySubmission[]
  loading: boolean
  error: string | null
  /** True when the failure is "no bearer token" — the pre-sign-in state. */
  authRequired: boolean
}

export function humanizeDiscoveryError(err: unknown): string {
  if (err instanceof ApiError) {
    const msg = err.message || err.code
    // Only claim "not migrated" when the error is actually a missing
    // relation — a transient failure must surface its real message.
    if (/relation .* does not exist/i.test(msg) || /could not find the table/i.test(msg)) {
      return 'The intake backend is not migrated yet — apply the sq_intake_invites migration to enable this.'
    }
    return msg
  }
  return 'Could not reach the intake backend.'
}

/**
 * SSR-safe absolute URL for an invite's public receiver page, built at
 * call time (never at render/module time) so window is only touched in
 * the browser. Accepts either the server-issued public_path
 * ("/square/intake/<code>") or a bare invite_code.
 */
export function publicIntakeUrl(pathOrCode: string): string {
  const path = pathOrCode.startsWith('/') ? pathOrCode : `/square/intake/${pathOrCode}`
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  return `${origin}${path}`
}

export interface CreateInviteDraft {
  title: string
  invite_type?: 'website' | 'software' | 'presentation' | 'other' | 'custom'
  description?: string
  expires_at?: string
}

// ── Atomic client-intake (client + project + binding + receipt) ──
export type ClientProjectType =
  'website' | 'software' | 'presentation' | 'operations' | 'legal' | 'marketing' | 'event' | 'other'

export interface CreateClientProjectDraft {
  client_name: string
  project_name: string
  contact_email?: string
  contact_phone?: string
  project_type?: ClientProjectType
  project_slug?: string
}

// One confirmed dependency in the quantum-sync chain (drives the receipt).
export interface QuantumSyncCheck {
  key: string
  label: string
  status: string
  id?: string
}

export interface ClientProjectResult {
  ok: true
  client: Record<string, unknown> & { id?: string; name?: string }
  project: Record<string, unknown> & { id?: string; name?: string; slug?: string }
  receipt: Record<string, unknown> & { id?: string; summary?: string }
  sync: {
    client_created: boolean
    project_created: boolean
    binding_confirmed: boolean
    receipt_written: boolean
    checks: QuantumSyncCheck[]
  }
}

// The statuses that count a dependency as CONFIRMED in the quantum-sync
// receipt. Anything else (pending/syncing/skipped/blocked) is not confirmed.
const QUANTUM_SYNC_CONFIRMED = new Set(['verified', 'connected', 'sealed'])

/**
 * The receipt's "X / N synchronized" gate — the single source of truth for
 * whether the whole dependency chain confirmed. `sealed` is true only when
 * every check reached a confirmed status (never rolls a pending/blocked step
 * into a green summary). Pure + exported so the honesty gate is testable.
 */
export function summarizeQuantumSync(
  checks: QuantumSyncCheck[] | null | undefined,
): { done: number; total: number; sealed: boolean } {
  const list = Array.isArray(checks) ? checks : []
  const done = list.filter((c) => QUANTUM_SYNC_CONFIRMED.has(c.status)).length
  return { done, total: list.length, sealed: list.length > 0 && done === list.length }
}

export function useDiscovery() {
  const [state, setState] = useState<DiscoveryState>({
    invites: [],
    submissions: [],
    loading: true,
    error: null,
    authRequired: false,
  })

  const load = useCallback(async (signal?: AbortSignal) => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const [inv, subs] = await Promise.all([api.intake.invites(), api.intake.submissions()])
      if (signal?.aborted) return
      setState({
        invites: (inv.invites || []) as unknown as DiscoveryInvite[],
        submissions: (subs.submissions || []) as unknown as DiscoverySubmission[],
        loading: false,
        error: null,
        authRequired: false,
      })
    } catch (err) {
      if (signal?.aborted) return
      if (isAuthError(err)) {
        setState({ invites: [], submissions: [], loading: false, error: null, authRequired: true })
      } else {
        setState({ invites: [], submissions: [], loading: false, error: humanizeDiscoveryError(err), authRequired: false })
      }
    }
  }, [])

  useEffect(() => {
    const ac = new AbortController()
    void load(ac.signal)
    return () => ac.abort()
  }, [load])

  /** Create a real sq_intake_invites row; returns { invite, public_path }. Reloads. */
  const createInvite = useCallback(async (draft: CreateInviteDraft) => {
    const result = await api.intake.createInvite(draft)
    await load()
    return result
  }, [load])

  /** Revoke (status → closed) a real invite by row id. Audited server-side. Reloads. */
  const revokeInvite = useCallback(async (invite_id: string) => {
    const result = await api.intake.revokeInvite(invite_id)
    await load()
    return result
  }, [load])

  /** Decide a submission: 'pending' | 'resolved' | 'closed'. Audited. Reloads. */
  const decideSubmission = useCallback(async (submission_id: string, decision: 'pending' | 'resolved' | 'closed') => {
    const result = await api.intake.decideSubmission({ submission_id, decision })
    await load()
    return result
  }, [load])

  /** Convert a submission into a real sq_projects row. Audited. Reloads. */
  const convertToProject = useCallback(async (submission_id: string) => {
    const result = await api.intake.convertToProject(submission_id)
    await load()
    return result
  }, [load])

  /**
   * ATOMIC create — a real client + its project + the binding + a persisted
   * quantum-sync receipt, in one server transaction. Throws (ApiError) on
   * any failed dependency; the caller renders the blocked step. Reloads so
   * the new project surfaces immediately.
   */
  const createClientProject = useCallback(async (draft: CreateClientProjectDraft): Promise<ClientProjectResult> => {
    const result = (await api.intake.createClientProject(draft)) as ClientProjectResult
    await load()
    return result
  }, [load])

  return { ...state, reload: () => load(), createInvite, revokeInvite, decideSubmission, convertToProject, createClientProject }
}
